import { toTitleCase } from 'features/dynamicOntology'
import { formatRaw } from 'features/libra/llmDiff/formatFieldValue'
import { unwrapFieldValue } from 'features/libra/ontology/ontologyFieldDefaults'
import type {
  FieldValues,
  OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import {
  type ComputedSection,
  getComputedFieldId,
} from 'features/libra/sections/computedSection/computedSectionTypes'
import { evaluateComputedSectionWithOverride } from 'features/libra/stepComponents/conditionalForm/computed/evaluateComputedSection'

// Only enum codes are title-cased; recorded free text must remain verbatim.
export function formatReportFieldValue(
  value: unknown,
  field?: OntologyField
): string {
  if (
    field &&
    'type' in field &&
    field.type === 'enum' &&
    typeof value === 'string' &&
    value !== ''
  ) {
    return toTitleCase(value)
  }
  return formatRaw(value)
}

function formatComputedResult(
  section: ComputedSection,
  value: unknown
): string {
  if (typeof value === 'boolean' && section.presentation) {
    return value
      ? (section.presentation.trueLabel ?? 'Pass')
      : (section.presentation.falseLabel ?? 'Fail')
  }
  return formatReportFieldValue(value)
}

export function buildComputedReportRows(
  section: ComputedSection,
  ontologyFields: Record<string, OntologyField>,
  fieldValues: FieldValues
) {
  const evaluation = evaluateComputedSectionWithOverride(section, fieldValues, {
    ontologyFields,
  })
  const rows: Array<{ label: string; value: string }> = []
  if (evaluation.finalResult !== undefined) {
    rows.push({
      label: 'Result',
      value: formatComputedResult(section, evaluation.finalResult),
    })
  }
  if (evaluation.isOverridden) {
    rows.push({
      label: 'Override',
      value: formatComputedResult(section, evaluation.overrideValue),
    })
  }
  if (evaluation.overrideJustification) {
    rows.push({
      label: 'Override reason',
      value: evaluation.overrideJustification,
    })
  }
  const outcomeFieldId = getComputedFieldId(section)
  const stored = unwrapFieldValue(fieldValues[outcomeFieldId])
  if (
    stored !== undefined &&
    stored !== null &&
    stored !== evaluation.finalResult &&
    formatReportFieldValue(stored, ontologyFields[outcomeFieldId]) !== '—'
  ) {
    rows.push({
      label: ontologyFields[outcomeFieldId]?.name ?? outcomeFieldId,
      value: formatReportFieldValue(stored, ontologyFields[outcomeFieldId]),
    })
  }
  return rows
}
